// FeaturesAtoZ — the full A-Z capability list. A scrollable grid of 26
// short feature entries that serves both as marketing scaffold and the
// canonical product feature roster. Living document — keys come from
// `features_az.items` in en.json so the team can edit copy without code.
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ForgetMeNotIcon } from "../ForgetMeNotIcon";
import { FmnWatermark } from "../FmnWatermark";

const FADE = {
    initial: { opacity: 0, y: 22 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.85, ease: [0.16, 1, 0.3, 1] },
};

export function FeaturesAtoZ() {
    const { t } = useTranslation();
    const items = t("features_az.items", { returnObjects: true });
    const list = Array.isArray(items) ? items : [];

    return (
        <section
            className="relative py-32 px-6 bg-white overflow-hidden"
            data-testid="features-az-section"
            id="features"
        >
            <FmnWatermark position="left" size={760} opacity={0.05} rotate={12} />
            <FmnWatermark position="bottom-right" size={620} opacity={0.05} rotate={-16} />
            <div className="relative max-w-7xl mx-auto">
                <motion.div {...FADE} className="text-center max-w-3xl mx-auto mb-16">
                    <div className="flex justify-center mb-6">
                        <ForgetMeNotIcon size={44} />
                    </div>
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-5">
                        {t("features_az.eyebrow")}
                    </p>
                    <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2D2B2A] leading-[1.06] tracking-tight">
                        {t("features_az.headline_part1")}{" "}
                        <em className="italic text-[#4A5A95]">{t("features_az.headline_emphasis")}</em>
                    </h2>
                    <p className="mt-7 text-lg text-[#5C5A59] leading-relaxed font-light">
                        {t("features_az.lead")}
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
                    {list.map((item, i) => (
                        <motion.div
                            key={item.k}
                            initial={{ opacity: 0, y: 18 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.7, delay: (i % 6) * 0.05, ease: [0.16, 1, 0.3, 1] }}
                            className="rounded-2xl bg-[#FDFBF7] border border-[#E5E1D8] p-6 hover:border-[#8C9ECB]/60 hover:-translate-y-0.5 transition-all"
                            data-testid={`feature-${item.k.toLowerCase()}`}
                        >
                            <div className="flex items-start gap-4">
                                <div className="shrink-0 w-11 h-11 rounded-xl bg-white border border-[#E5E1D8] flex items-center justify-center font-serif text-2xl italic text-[#4A5A95]">
                                    {item.k}
                                </div>
                                <div className="min-w-0">
                                    <h3 className="font-serif text-lg text-[#2D2B2A] leading-snug mb-1.5">
                                        {item.title}
                                    </h3>
                                    <p className="text-sm text-[#5C5A59] leading-relaxed">
                                        {item.body}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                <motion.p
                    {...FADE}
                    transition={{ ...FADE.transition, delay: 0.3 }}
                    className="mt-14 text-center text-sm text-[#8A8786] italic max-w-2xl mx-auto"
                >
                    {t("features_az.footnote")}
                </motion.p>
            </div>
        </section>
    );
}
